import { MUITab } from "@abb/arcadia-ui-react"
import { useContext, useEffect, useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { digitalManagerApi } from "../../../../services/api"
import {
  DiMaDetailsHeader,
  DiMaDetailsHeaderProps,
} from "../../../components/DiMaDetailsHeader"
import { DiMaProgress } from "../../../components/DiMaProgress"
import { UserContext } from "../../../context/UserContext"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToFeature,
} from "../../../models/Authorization"
import { UserGet, UserInfo } from "../../../models/User"
import { useHashTab } from "../../../../hooks/useHashTab"
import { UserApplications } from "./applications_tab/UserApplications"
import { UserApproverRoles } from "./approver_roles_tab/UserApproverRoles"
import { UserApplicationsPermissions } from "./apps_permissions_tab/UserApplicationsPermissions"
import { UserApplicationsRoles } from "./apps_roles_tab/UserApplicationsRoles"
import { UserBackofficeAccess } from "./backoffice_access_tab/UserBackofficeAccess"
import { UserDetails } from "./details_tab/UserDetails"

const returnURL = "/users"

const userHasRoleManagerReadAccess = (user: UserInfo | undefined) =>
  user?.authorizations.applications.find((app) => app.featuresAccesses["RM"]) != null

const userHasApplicationManagementReadAccess = (user: UserInfo | undefined) =>
  user?.authorizations.applications.find((app) => app.featuresAccesses["AM"]) != null

export function UserContainer() {
  const { user, isLoadingUser } = useContext(UserContext)

  const { userId } = useParams()
  const navigate = useNavigate()

  const { activeTab, handleTabChange } = useHashTab("details")
  const [selectedUser, setSelectedUser] = useState<UserGet>()

  useEffect(() => {
    if (userId) {
      digitalManagerApi
        .get<UserGet>(`/users/${userId}`)
        .then((res) => {
          setSelectedUser(res.data)
        })
        .catch(() => {
          navigate(returnURL)
        })
    }
  }, [navigate, userId])

  if (!userId) {
    return <Navigate to={returnURL} />
  }

  if (!selectedUser || !user || isLoadingUser) {
    return <DiMaProgress />
  }

  const userHasBackofficeReadAccess = hasUserAccessToFeature(
    user.authorizations,
    DimaFeatureShortNameEnum.BA,
    AccessLevel.Read
  )

  const detailsHeaderProps: DiMaDetailsHeaderProps = {
    url: returnURL,
    title:
      selectedUser.first && selectedUser.last
        ? `${selectedUser.first} ${selectedUser.last}`
        : "No name available",
    label: "User ID",
    subtitle: selectedUser.id,
  }

  return (
    <>
      {selectedUser && (
        <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
          <DiMaDetailsHeader {...detailsHeaderProps} />
          <MUITab
            tabItems={[
              { id: "details", label: "Details" },
              ...(userHasRoleManagerReadAccess(user)
                ? [{ id: "apps-roles", label: "Apps Roles" }]
                : []),
              ...(userHasRoleManagerReadAccess(user)
                ? [{ id: "apps-permissions", label: "Apps Permissions" }]
                : []),
              ...(userHasApplicationManagementReadAccess(user)
                ? [{ id: "approver-roles", label: "Approver Roles" }]
                : []),
              ...(userHasRoleManagerReadAccess(user)
                ? [{ id: "applications", label: "Applications" }]
                : []),
              ...(userHasBackofficeReadAccess
                ? [{ id: "backoffice-access", label: "Backoffice Access" }]
                : []),
            ]}
            value={activeTab}
            handleChange={handleTabChange}
          />

          {activeTab === "details" && (
            <UserDetails
              user={selectedUser}
              loggedUser={user}
              setSelectedUser={setSelectedUser}
            />
          )}

          {userHasRoleManagerReadAccess(user) && activeTab === "apps-roles" && (
            <UserApplicationsRoles />
          )}

          {userHasRoleManagerReadAccess(user) && activeTab === "apps-permissions" && (
            <UserApplicationsPermissions />
          )}

          {userHasApplicationManagementReadAccess(user) &&
            activeTab === "approver-roles" && <UserApproverRoles />}

          {userHasRoleManagerReadAccess(user) && activeTab === "applications" && (
            <UserApplications />
          )}

          {userHasBackofficeReadAccess && activeTab === "backoffice-access" && (
            <UserBackofficeAccess />
          )}
        </div>
      )}
    </>
  )
}
